import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Stack,
    Button,
    Grid,
    CircularProgress,
    Container,
    Avatar,
    Chip,
    Divider,
    CardMedia,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import EventIcon from "@mui/icons-material/Event";
import InsightsIcon from "@mui/icons-material/Insights";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { getSingleReport } from "../../Slices/reports";

import UpcomingEventsCard from "../../Components/reports/UpcomingEvents";
import SkillsSummaryChart from "../../Components/reports/SkillsSummaryChart";
import ComparisonBarChart from "../../Components/reports/ComparisonBarChart";
import PopularQualifications from "../../Components/reports/PopularQualifications";
import ChildDevelopmentSuggestions from "../../Components/reports/ChildDevelopmentSuggestions";
import PastEventsCard from "../../Components/reports/PastEvents";
import CareerAIAdvisorCard from "../../Components/reports/AIAdvice";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { useTheme } from "@mui/material/styles";


const getBadgeCount = (report) => {
    return (
        (report?.earnedCredsGold ?? 0) +
        (report?.earnedCredsSilver ?? 0) +
        (report?.earnedCredsBronze ?? 0)
    );
};



const SectionHeader = ({
    icon,
    title,
    subtitle,
    action,
}) => {

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            mb={3}
        >
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2.5,
                        background: "#eef6f8",
                        color: "#2f8492",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    {icon}
                </Box>

                <Box>
                    <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{
                            lineHeight: 1.2,
                        }}
                    >
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.4 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Stack>

            {action}
        </Stack>
    );
};


const KpiCard = ({
    gradient,
    icon,
    label,
    value,
    subtitle,
}) => {
    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",
                borderRadius: 4,
                position: "relative",
                overflow: "hidden",
                background: "#fff",
                border: "1px solid",
                borderColor: "rgba(15, 23, 42, 0.08)",
                transition: "all .2s ease",

                "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow:
                        "0 12px 30px rgba(15, 23, 42, .08)",
                },

                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 5,
                    background: gradient,
                },
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                >
                    <Box
                        sx={{
                            width: 54,
                            height: 54,
                            borderRadius: 3,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f8fafc",
                            color: "#475569",
                            flexShrink: 0,
                        }}
                    >
                        {icon}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight={600}
                        >
                            {label}
                        </Typography>

                        <Typography
                            variant="h3"
                            fontWeight={800}
                            lineHeight={1.1}
                            sx={{ mt: 0.5 }}
                        >
                            {value}
                        </Typography>

                        {subtitle && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};


const BadgeCard = ({ badge }) => {
    const isRevoked = badge?.status === "revoke";
    const theme = useTheme();

    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",
                borderRadius: 3,
                border: "1px solid #e2e8f0",
                transition: "all .2s ease",
                opacity: isRevoked ? 0.65 : 1,

                "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow:
                        "0 10px 25px rgba(15, 23, 42, .08)",
                },
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                >
                    <Box
                        sx={{
                            width: 76,
                            height: 76,
                            borderRadius: 3,
                            backgroundColor:
                                theme.palette.mode === "dark"
                                    ? theme.palette.background.main
                                    : "rgba(255, 255, 255, 0.58)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={badge?.image?.url}
                            alt={badge?.badgeName || "Odznaka"}
                            sx={{
                                width: 62,
                                height: 66,
                                objectFit: "contain",
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        <Typography
                            fontWeight={800}
                            sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        >
                            {badge?.badgeName || "Bez nazwy"}
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                        >
                            {badge?.badgeType || "Mikropoświadczenie"}
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={0.7}
                            mt={1}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            {badge?.prkLevel && (
                                <Chip
                                    size="small"
                                    label={`PRK ${badge.prkLevel}`}
                                    sx={{
                                        height: 24,
                                        fontSize: 11,
                                        fontWeight: 700,
                                    }}
                                />
                            )}

                            <Chip
                                size="small"
                                label={
                                    isRevoked
                                        ? "Unieważniona"
                                        : "Aktywna"
                                }
                                sx={{
                                    height: 24,
                                    fontSize: 11,
                                    fontWeight: 700,
                                }}
                            />
                        </Stack>
                    </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {badge?.qualificationName
                        ? badge.qualificationName
                        : badge?.badgeType === "Odznaka srebrna"
                            ? "Zestaw efektów"
                            : "Osiągnięcie własne"}
                </Typography>
            </CardContent>
        </Card>
    );
};


const ReportSingle = () => {
    const {
        user,
        accessToken: token,
    } = useSelector((state) => state.auth);

    const { id } = useParams();

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);

    const viewRef = useRef();
    const containerRef = useRef();
    console.log(report)

    useEffect(() => {
        if (!token) return;

        const fetchReport = async () => {
            try {
                setLoading(true);

                const res = await getSingleReport(id, token);

                console.log("REPORT:", res?.reportData);

                setReport(res?.reportData);
            } catch (err) {
                console.error(
                    "Błąd pobierania raportu:",
                    err
                );
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
    }, [id, token]);



    if (loading || !report) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f8fafc",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }


    const generatePdf = async () => {
        if (!containerRef.current) return;

        try {
            const canvas = await html2canvas(
                containerRef.current,
                {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: "#f8fafc",
                }
            );

            const imgData = canvas.toDataURL(
                "image/jpeg",
                1
            );

            const pdf = new jsPDF({
                orientation: "p",
                unit: "mm",
                format: "a4",
                compress: true,
            });

            const pdfWidth =
                pdf.internal.pageSize.getWidth();

            const pdfHeight =
                pdf.internal.pageSize.getHeight();

            const imgProps =
                pdf.getImageProperties(imgData);

            const scaledWidth = pdfWidth;

            const scaledHeight =
                (imgProps.height * pdfWidth) /
                imgProps.width;

            let remainingHeight = scaledHeight;
            let y = 0;

            while (remainingHeight > 0) {
                pdf.addImage(
                    imgData,
                    "JPEG",
                    0,
                    -y,
                    scaledWidth,
                    scaledHeight
                );

                remainingHeight -= pdfHeight;

                if (remainingHeight > 0) {
                    pdf.addPage();
                    y += pdfHeight;
                }
            }

            pdf.save(
                `raport-${report?.user?.name || "student"}.pdf`
            );
        } catch (error) {
            console.error(
                "Błąd generowania PDF:",
                error
            );
        }
    };


    const badgeCount = getBadgeCount(report);

    const hardSkills =
        report?.skillsSumarize?.hardSkills ?? 0;

    const softSkills =
        report?.skillsSumarize?.softSkills ?? 0;

    const totalSkills =
        report?.skillsSumarize?.total ?? 0;

    const prkUser =
        report?.comparison?.prkUser ?? 0;

    const prkAverage =
        report?.comparison?.prkAverage ?? 0;

    const badgesAverage =
        report?.comparison?.badgesAverage ?? 0;


    return (
        <Container
            ref={containerRef}
            maxWidth="xl"
            sx={{
                py: {
                    xs: 1,
                    sm: 3,
                    md: 5,
                },
            }}
        >
            <Box
                ref={viewRef}
                sx={{
                    background: "#f8fafc",
                    minHeight: "100vh",
                    borderRadius: {
                        xs: 0,
                        md: 5,
                    },
                    p: {
                        xs: 1,
                        sm: 2,
                        md: 4,
                    },
                }}
            >

                {/* <Card
                    elevation={0}
                    sx={{
                        mb: 3,
                        borderRadius: 5,
                        overflow: "hidden",
                        background:
                            "linear-gradient(135deg, #11334b 0%, #1f5d78 55%, #2f8492 100%)",
                        color: "#fff",
                        position: "relative",
                    }}
                >
                    <CardContent
                        sx={{
                            p: {
                                xs: 3,
                                md: 4,
                            },
                        }}
                    >
                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            alignItems={{
                                xs: "flex-start",
                                sm: "center",
                            }}
                            justifyContent="space-between"
                            spacing={3}
                        >
                            <Stack
                                direction="row"
                                spacing={2.5}
                                alignItems="center"
                            >
                                <Avatar
                                    sx={{
                                        width: 72,
                                        height: 72,
                                        bgcolor:
                                            "rgba(255,255,255,.15)",
                                        color: "#fff",
                                        fontSize: 28,
                                        fontWeight: 700,
                                        border:
                                            "2px solid rgba(255,255,255,.3)",
                                    }}
                                >
                                    {report?.user?.name
                                        ?.charAt(0)
                                        ?.toUpperCase() ?? ""}
                                </Avatar>

                                <Box>
                                    <Typography
                                        variant="overline"
                                        sx={{
                                            opacity: 0.75,
                                            letterSpacing: 1.5,
                                            fontWeight: 700,
                                        }}
                                    >
                                        RAPORT KOMPETENCJI
                                    </Typography>

                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        sx={{
                                            fontSize: {
                                                xs: "1.7rem",
                                                md: "2.2rem",
                                            },
                                        }}
                                    >
                                        {report?.user?.name}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.5,
                                            opacity: 0.85,
                                        }}
                                    >
                                        Ścieżka kompetencyjna
                                        Twojego dziecka
                                    </Typography>
                                </Box>
                            </Stack>

                            <Box
                                sx={{
                                    px: 2,
                                    py: 1,
                                    borderRadius: 3,
                                    background:
                                        "rgba(255,255,255,.12)",
                                    border:
                                        "1px solid rgba(255,255,255,.15)",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        opacity: 0.7,
                                    }}
                                >
                                    RAPORT
                                </Typography>

                                <Typography fontWeight={700}>
                                    Rozwój kompetencji
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card> */}

                <Card
                    elevation={0}
                    sx={{
                        position: "relative",
                        overflow: "hidden",
                        mb: 3,
                        borderRadius: "24px",
                        color: "#fff",
                        background:
                            "linear-gradient(135deg, #111827 0%, #283E70 55%, #2f8492 100%)",
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            width: 350,
                            height: 350,
                            borderRadius: "50%",
                            right: -120,
                            top: -180,
                            bgcolor: "rgba(255,255,255,0.08)",
                        }}
                    />

                    <Box
                        sx={{
                            position: "absolute",
                            width: 220,
                            height: 220,
                            borderRadius: "50%",
                            right: 120,
                            bottom: -170,
                            bgcolor: "rgba(255,255,255,0.05)",
                        }}
                    />

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            p: { xs: 3, md: 4 },
                            display: "flex",
                            flexDirection: {
                                xs: "column",
                                md: "row",
                            },
                            alignItems: {
                                xs: "flex-start",
                                md: "center",
                            },
                            justifyContent: "space-between",
                            gap: 3,
                        }}
                    >
                        <Box>
                            <Chip
                                label="RAPORT RODZICA"
                                sx={{
                                    mb: 2,
                                    bgcolor: "rgba(255,255,255,0.14)",
                                    color: "#fff",
                                    fontWeight: 700,
                                    letterSpacing: ".08em",
                                    fontSize: "0.72rem",
                                    backdropFilter: "blur(10px)",
                                }}
                            />

                            <Typography
                                variant="h3"
                                sx={{
                                    fontSize: {
                                        xs: "1.8rem",
                                        md: "2.5rem",
                                    },
                                    lineHeight: 1.15,
                                    fontWeight: 800,
                                    maxWidth: 700,
                                }}
                            >
                                Ścieżka kompetencyjna
                                <Box
                                    component="span"
                                    sx={{
                                        display: "block",
                                        color: "#A5B4FC",
                                    }}
                                >
                                    Twojego dziecka
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 2,
                                    color: "rgba(255,255,255,.72)",
                                    maxWidth: 620,
                                }}
                            >
                                Postępy, zdobyte kompetencje, mikropoświadczenia
                                oraz rekomendacje rozwoju w jednym miejscu.
                            </Typography>
                        </Box>

                        <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                            sx={{
                                bgcolor: "rgba(255,255,255,.10)",
                                border: "1px solid rgba(255,255,255,.15)",
                                px: 2.5,
                                py: 2,
                                borderRadius: "18px",
                                backdropFilter: "blur(12px)",
                                minWidth: { md: 260 },
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 58,
                                    height: 58,
                                    bgcolor: "#fff",
                                    color: "#4F46E5",
                                    fontSize: "1.45rem",
                                    fontWeight: 800,
                                }}
                            >
                                {report?.user?.name
                                    ?.charAt(0)
                                    ?.toUpperCase() ?? ""}
                            </Avatar>

                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "rgba(255,255,255,.65)",
                                    }}
                                >
                                    Profil dziecka
                                </Typography>

                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 700,
                                    }}
                                >
                                    {report?.user?.name ?? ""}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Card>



                {/* <Grid
                    container
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                    >
                        <KpiCard
                            gradient="#D4AF37"
                            icon={
                                <WorkspacePremiumIcon />
                            }
                            label="Złote odznaki"
                            value={
                                report?.earnedCredsGold ??
                                0
                            }
                            subtitle="kwalifikacje"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                    >
                        <KpiCard
                            gradient="#94A3B8"
                            icon={
                                <EmojiEventsIcon />
                            }
                            label="Srebrne odznaki"
                            value={
                                report?.earnedCredsSilver ??
                                0
                            }
                            subtitle="osiągnięcia własne"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                    >
                        <KpiCard
                            gradient="#A16207"
                            icon={
                                <WorkspacePremiumIcon />
                            }
                            label="Brązowe odznaki"
                            value={
                                report?.earnedCredsBronze ??
                                0
                            }
                            subtitle="kompetencje VR"
                        />
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={3}
                    >
                        <KpiCard
                            gradient="#2F8492"
                            icon={
                                <PsychologyIcon />
                            }
                            label="Kompetencje"
                            value={totalSkills}
                            subtitle="efekty uczenia się"
                        />
                    </Grid>
                </Grid> */}

                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 800,
                            color: "#111827",
                            mb: 0.5,
                        }}
                    >
                        Mikropoświadczenia
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            color: "#6B7280",
                            mb: 2.5,
                        }}
                    >
                        Podsumowanie zdobytych odznak i kwalifikacji.
                    </Typography>

                    <Grid container spacing={2.5}>

                        <Grid item xs={12} md={4}>
                            <Card
                                elevation={0}
                                sx={{
                                    position: "relative",
                                    height: "100%",
                                    overflow: "hidden",
                                    borderRadius: "22px",
                                    color: "#fff",

                                    background: `
            radial-gradient(
                circle at 100% 0%,
                rgba(255, 208, 28, 0.95) 0%,
                rgba(255, 208, 28, 0.55) 12%,
                rgba(255, 208, 28, 0.18) 28%,
                rgba(255, 208, 28, 0.04) 42%,
                transparent 58%
            ),
            linear-gradient(
                135deg,
                #12192a 0%,
                #2a4b76 55%,
                #1f315b 100%
            )
        `,

                                    border: "1px solid rgba(255, 208, 28, 0.28)",

                                    boxShadow:
                                        "0 10px 30px rgba(17, 24, 39, 0.16)",

                                    transition:
                                        "transform .25s ease, box-shadow .25s ease, border-color .25s ease",

                                    "&:hover": {
                                        transform: "translateY(-5px)",
                                        borderColor: "rgba(255, 208, 28, 0.55)",
                                        boxShadow:
                                            "0 18px 40px rgba(17, 24, 39, 0.28)",
                                    },

                                    "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        width: 180,
                                        height: 180,
                                        borderRadius: "50%",
                                        right: -100,
                                        bottom: -100,
                                        background: "rgba(255,255,255,0.035)",
                                    },
                                }}
                            >
                                <CardContent
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        p: 3,
                                        "&:last-child": {
                                            pb: 3,
                                        },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                    >
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#FFD01C",
                                                    fontWeight: 800,
                                                    letterSpacing: ".08em",
                                                    fontSize: "0.72rem",
                                                }}
                                            >
                                                KWALIFIKACJE
                                            </Typography>

                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    mt: 1,
                                                    fontWeight: 800,
                                                    color: "#fff",
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {report?.earnedCredsGold ?? 0}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                width: 68,
                                                height: 68,
                                                borderRadius: "18px",

                                                bgcolor: "rgba(255,255,255,.08)",

                                                border:
                                                    "1px solid rgba(255,255,255,.15)",

                                                display: "grid",
                                                placeItems: "center",

                                                backdropFilter: "blur(12px)",

                                                boxShadow:
                                                    "0 8px 20px rgba(0,0,0,.16)",
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                image="/assets/badge_gold.png"
                                                alt="Złote odznaki"
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </Box>
                                    </Stack>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 2.5,
                                            color: "rgba(255,255,255,.72)",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Złote odznaki
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>


                        <Grid item xs={12} md={4}>
                            <Card
                                elevation={0}
                                sx={{
                                    position: "relative",
                                    height: "100%",
                                    overflow: "hidden",
                                    borderRadius: "22px",
                                    color: "#fff",

                                    background: `
                radial-gradient(
                    circle at 100% 0%,
                    rgba(220, 226, 235, 0.95) 0%,
                    rgba(220, 226, 235, 0.55) 12%,
                    rgba(220, 226, 235, 0.18) 28%,
                    rgba(220, 226, 235, 0.04) 42%,
                    transparent 58%
                ),
                linear-gradient(
                    135deg,
                    #12192a 0%,
                    #2a4b76 55%,
                    #1f315b 100%
                )
            `,

                                    border: "1px solid rgba(220, 226, 235, 0.28)",

                                    boxShadow:
                                        "0 10px 30px rgba(17, 24, 39, 0.16)",

                                    transition:
                                        "transform .25s ease, box-shadow .25s ease, border-color .25s ease",

                                    "&:hover": {
                                        transform: "translateY(-5px)",
                                        borderColor: "rgba(220, 226, 235, 0.55)",
                                        boxShadow:
                                            "0 18px 40px rgba(17, 24, 39, 0.28)",
                                    },

                                    "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        width: 180,
                                        height: 180,
                                        borderRadius: "50%",
                                        right: -100,
                                        bottom: -100,
                                        background: "rgba(255,255,255,0.035)",
                                    },
                                }}
                            >
                                <CardContent
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        p: 3,
                                        "&:last-child": {
                                            pb: 3,
                                        },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                    >
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#DDE3EB",
                                                    fontWeight: 800,
                                                    letterSpacing: ".08em",
                                                    fontSize: "0.72rem",
                                                }}
                                            >
                                                EFEKTY UCZENIA SIĘ
                                            </Typography>

                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    mt: 1,
                                                    fontWeight: 800,
                                                    color: "#fff",
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {report?.earnedCredsSilver ?? 0}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                width: 68,
                                                height: 68,
                                                borderRadius: "18px",

                                                bgcolor: "rgba(255,255,255,.08)",

                                                border:
                                                    "1px solid rgba(255,255,255,.15)",

                                                display: "grid",
                                                placeItems: "center",

                                                backdropFilter: "blur(12px)",

                                                boxShadow:
                                                    "0 8px 20px rgba(0,0,0,.16)",
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                image="/assets/badge_silver.png"
                                                alt="Srebrne odznaki"
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </Box>
                                    </Stack>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 2.5,
                                            color: "rgba(255,255,255,.72)",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Srebrne odznaki
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>


                        <Grid item xs={12} md={4}>
                            <Card
                                elevation={0}
                                sx={{
                                    position: "relative",
                                    height: "100%",
                                    overflow: "hidden",
                                    borderRadius: "22px",
                                    color: "#fff",

                                    background: `
                radial-gradient(
                    circle at 100% 0%,
                    rgba(193, 128, 78, 0.95) 0%,
                    rgba(193, 128, 78, 0.55) 12%,
                    rgba(193, 128, 78, 0.18) 28%,
                    rgba(193, 128, 78, 0.04) 42%,
                    transparent 58%
                ),
                linear-gradient(
                    135deg,
                    #12192a 0%,
                    #2a4b76 55%,
                    #1f315b 100%
                )
            `,

                                    border: "1px solid rgba(193, 128, 78, 0.28)",

                                    boxShadow:
                                        "0 10px 30px rgba(17, 24, 39, 0.16)",

                                    transition:
                                        "transform .25s ease, box-shadow .25s ease, border-color .25s ease",

                                    "&:hover": {
                                        transform: "translateY(-5px)",
                                        borderColor: "rgba(193, 128, 78, 0.55)",
                                        boxShadow:
                                            "0 18px 40px rgba(17, 24, 39, 0.28)",
                                    },

                                    "&::after": {
                                        content: '""',
                                        position: "absolute",
                                        width: 180,
                                        height: 180,
                                        borderRadius: "50%",
                                        right: -100,
                                        bottom: -100,
                                        background: "rgba(255,255,255,0.035)",
                                    },
                                }}
                            >
                                <CardContent
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        p: 3,
                                        "&:last-child": {
                                            pb: 3,
                                        },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                    >
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#D29A70",
                                                    fontWeight: 800,
                                                    letterSpacing: ".08em",
                                                    fontSize: "0.72rem",
                                                }}
                                            >
                                                OSIĄGNIĘCIA WŁASNE
                                            </Typography>

                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    mt: 1,
                                                    fontWeight: 800,
                                                    color: "#fff",
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {report?.earnedCredsBronze ?? 0}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                width: 68,
                                                height: 68,
                                                borderRadius: "18px",

                                                bgcolor: "rgba(255,255,255,.08)",

                                                border:
                                                    "1px solid rgba(255,255,255,.15)",

                                                display: "grid",
                                                placeItems: "center",

                                                backdropFilter: "blur(12px)",

                                                boxShadow:
                                                    "0 8px 20px rgba(0,0,0,.16)",
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                image="/assets/badge_brown.png"
                                                alt="Brązowe odznaki"
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    objectFit: "contain",
                                                }}
                                            />
                                        </Box>
                                    </Stack>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 2.5,
                                            color: "rgba(255,255,255,.72)",
                                            fontWeight: 600,
                                        }}
                                    >
                                        Brązowe odznaki
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                    </Grid>
                </Box>


                <Card
                    elevation={0}
                    sx={{
                        mb: 3,
                        borderRadius: 4,
                        border:
                            "1px solid #e2e8f0",
                    }}
                >
                    <CardContent
                        sx={{
                            p: {
                                xs: 2.5,
                                md: 3,
                            },
                        }}
                    >
                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={{
                                xs: 2,
                                sm: 0,
                            }}
                            justifyContent="space-around"
                            divider={
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                />
                            }
                        >
                            <Box textAlign="center">
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Wszystkie odznaki
                                </Typography>

                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                >
                                    {badgeCount}
                                </Typography>
                            </Box>

                            <Box textAlign="center">
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Umiejętności twarde
                                </Typography>

                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                >
                                    {hardSkills}
                                </Typography>
                            </Box>

                            <Box textAlign="center">
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Umiejętności miękkie
                                </Typography>

                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                >
                                    {softSkills}
                                </Typography>
                            </Box>

                            <Box textAlign="center">
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Poziom PRK
                                </Typography>

                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                >
                                    {prkUser}
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>


                <Card
                    elevation={0}
                    sx={{
                        mb: 3,
                        borderRadius: 4,
                        border:
                            "1px solid #e2e8f0",
                    }}
                >
                    <CardContent
                        sx={{
                            p: {
                                xs: 2,
                                md: 3,
                            },
                        }}
                    >
                        <SectionHeader
                            icon={
                                <WorkspacePremiumIcon />
                            }
                            title="Zdobyte mikropoświadczenia"
                            subtitle="Odznaki potwierdzające rozwój kompetencji"
                            action={
                                <Chip
                                    label={`${badgeCount} odznak`}
                                    sx={{
                                        fontWeight: 700,
                                    }}
                                />
                            }
                        />

                        <Grid
                            container
                            spacing={2}
                        >
                            {report?.badges?.map(
                                (badge) => (
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={4}
                                        key={badge.id}
                                    >
                                        <BadgeCard
                                            badge={badge}
                                        />
                                    </Grid>
                                )
                            )}
                        </Grid>

                        {!report?.badges?.length && (
                            <Box
                                sx={{
                                    py: 5,
                                    textAlign: "center",
                                }}
                            >
                                <Typography
                                    color="text.secondary"
                                >
                                    Brak zdobytych
                                    mikropoświadczeń.
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                <Grid
                    container
                    spacing={3}
                    sx={{ mb: 3 }}
                >


                    <Grid item xs={12} md={7}>
                        <Card
                            elevation={0}
                            sx={{
                                height: "100%",
                                borderRadius: 4,
                                border: "1px solid #e2e8f0",
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: 3,
                                    "&:last-child": {
                                        pb: 3,
                                    },
                                }}
                            >
                                <SectionHeader
                                    icon={<PsychologyIcon />}
                                    title="Profil kompetencji"
                                    subtitle="Efekty uczenia się Twojego dziecka"
                                />

                                <Box
                                    sx={{
                                        width: "100%",
                                        height: 320,
                                        minWidth: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            minWidth: 0,
                                        }}
                                    >
                                        <SkillsSummaryChart
                                            skills={report?.skillsSumarize}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        md={5}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                height: "100%",
                                borderRadius: 4,
                                border:
                                    "1px solid #e2e8f0",
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: 3,
                                }}
                            >
                                <SectionHeader
                                    icon={
                                        <InsightsIcon />
                                    }
                                    title="Na tle innych"
                                    subtitle="Porównanie z użytkownikami micree"
                                />

                                <Box
                                    sx={{
                                        minHeight: 280,
                                    }}
                                >
                                    {report?.comparison ? (
                                        <ComparisonBarChart
                                            comparison={
                                                report.comparison
                                            }
                                        />
                                    ) : (
                                        <Box
                                            sx={{
                                                minHeight: 250,
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                            }}
                                        >
                                            <Typography
                                                color="text.secondary"
                                            >
                                                Brak danych
                                                porównawczych
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                >
                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            PRK dziecka
                                        </Typography>

                                        <Typography
                                            fontWeight={800}
                                        >
                                            {prkUser}
                                        </Typography>
                                    </Box>

                                    <Box textAlign="right">
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            Średnia PRK
                                        </Typography>

                                        <Typography
                                            fontWeight={800}
                                        >
                                            {prkAverage}
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ mt: 2 }}
                                >
                                    <TrendingUpIcon
                                        fontSize="small"
                                        sx={{
                                            color:
                                                "#2f8492",
                                        }}
                                    />

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Średnia liczba
                                        odznak:{" "}
                                        <b>
                                            {
                                                badgesAverage
                                            }
                                        </b>
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>


                <Grid
                    container
                    spacing={3}
                    sx={{ mb: 3 }}
                >
                    <Grid
                        item
                        xs={12}
                        md={7}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                height: "100%",
                                borderRadius: 4,
                                border:
                                    "1px solid #e2e8f0",
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <SectionHeader
                                    icon={
                                        <SchoolIcon />
                                    }
                                    title="Popularne kwalifikacje"
                                    subtitle="Kwalifikacje najczęściej wybierane przez użytkowników micree"
                                />

                                <PopularQualifications
                                    popularQualifications={
                                        report?.popularQualifications
                                    }
                                />
                            </CardContent>
                        </Card>
                    </Grid>


                    <Grid
                        item
                        xs={12}
                        md={5}
                    >
                        <Card
                            elevation={0}
                            sx={{
                                height: "100%",
                                borderRadius: 4,
                                background:
                                    "linear-gradient(135deg, #eef7f8, #f8fafc)",
                                border:
                                    "1px solid #dceff1",
                            }}
                        >
                            <CardContent
                                sx={{
                                    p: 3,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="flex-start"
                                >
                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: 2.5,
                                            bgcolor:
                                                "#fff",
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",
                                            color:
                                                "#2f8492",
                                        }}
                                    >
                                        <AutoGraphIcon />
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="h6"
                                            fontWeight={800}
                                        >
                                            Kolejny krok
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                mt: 1,
                                                lineHeight: 1.7,
                                            }}
                                        >
                                            Na podstawie
                                            zdobytych
                                            kompetencji
                                            można rozważyć
                                            kolejne
                                            kwalifikacje,
                                            szkolenia oraz
                                            aktywności
                                            rozwojowe.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Card
                    elevation={0}
                    sx={{
                        mb: 3,
                        borderRadius: 4,
                        border:
                            "1px solid #e2e8f0",
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <SectionHeader
                            icon={<EventIcon />}
                            title="Aktywność i wydarzenia"
                            subtitle="Udział w szkoleniach i nadchodzące możliwości"
                        />

                        <Grid
                            container
                            spacing={3}
                        >
                            <Grid
                                item
                                xs={12}
                                md={6}
                            >
                                <PastEventsCard
                                    user={user}
                                    report={report}
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                            >
                                <UpcomingEventsCard
                                    user={user}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Box sx={{ mb: 3 }}>
                    <Stack
                        spacing={0.5}
                        mb={2.5}
                    >
                        <Typography
                            variant="h5"
                            fontWeight={800}
                        >
                            Plan dalszego rozwoju
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Sugestie i rekomendacje
                            dotyczące dalszego rozwoju
                        </Typography>
                    </Stack>

                    <Grid
                        container
                        spacing={3}
                    >
                        <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <Card
                                elevation={0}
                                sx={{
                                    height: "100%",
                                    borderRadius: 4,
                                    border:
                                        "1px solid #e2e8f0",
                                }}
                            >
                                <CardContent
                                    sx={{
                                        p: 3,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1.5}
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <Box
                                            sx={{
                                                width: 42,
                                                height: 42,
                                                borderRadius: 2.5,
                                                bgcolor:
                                                    "#f1f5f9",
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                            }}
                                        >
                                            <PsychologyIcon />
                                        </Box>

                                        <Box>
                                            <Typography
                                                variant="h6"
                                                fontWeight={800}
                                            >
                                                Sugestie rodzica
                                            </Typography>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                Własne obserwacje
                                                i pomysły
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Box sx={{ mt: 2 }}>
                                        <ChildDevelopmentSuggestions
                                            reportId={id}
                                            parentSuggestions={
                                                report?.parentSuggestions
                                            }
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>


                        <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <CareerAIAdvisorCard
                                studentData={report}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Stack
                    alignItems="center"
                    spacing={1}
                    sx={{
                        mt: 5,
                        pt: 4,
                        borderTop:
                            "1px solid #e2e8f0",
                    }}
                >
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={
                            <DownloadIcon />
                        }
                        onClick={generatePdf}
                        sx={{
                            borderRadius: 3,
                            px: 4,
                            py: 1.4,
                            textTransform: "none",
                            fontWeight: 700,
                            boxShadow: "none",
                            background:
                                "#11334b",

                            "&:hover": {
                                background:
                                    "#0c293d",
                                boxShadow:
                                    "0 8px 20px rgba(17,51,75,.2)",
                            },
                        }}
                    >
                        Pobierz raport PDF
                    </Button>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        Raport zostanie zapisany
                        w formacie PDF
                    </Typography>
                </Stack>

            </Box>
        </Container >
    );
};

export default ReportSingle;








// import {
//     Avatar,
//     Box,
//     Button,
//     Card,
//     CardContent,
//     CardMedia,
//     Chip,
//     CircularProgress,
//     Container,
//     Divider,
//     Grid,
//     Stack,
//     Typography,
// } from "@mui/material";

// import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
// import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
// import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
// import MilitaryTechRoundedIcon from "@mui/icons-material/MilitaryTechRounded";
// import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
// import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
// import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
// import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
// import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";

// import React, { useEffect, useRef, useState } from "react";

// import DownloadIcon from "@mui/icons-material/Download";
// import { useParams } from "react-router-dom";
// import { useSelector } from "react-redux";
// import { getSingleReport } from "../../Slices/reports";
// import StudentSkillProfileChartWeb from "../../Components/reports/RadarCompetencyChart";
// import UpcomingEventsCard from "../../Components/reports/UpcomingEvents";
// import SkillsSummaryChart from "../../Components/reports/SkillsSummaryChart";
// import ComparisonBarChart from "../../Components/reports/ComparisonBarChart";
// import PopularQualifications from "../../Components/reports/PopularQualifications";
// import ChildDevelopmentSuggestions from "../../Components/reports/ChildDevelopmentSuggestions";
// import jsPDF from "jspdf";
// import html2canvas from "html2canvas";
// import PastEventsCard from "../../Components/reports/PastEvents";
// import CareerAIAdvisorCard from "../../Components/reports/AIAdvice";


// const GradientCard = ({ gradient, children }) => (
//     <Card sx={{ background: gradient, color: "#fff", borderRadius: 12 }}>
//         {children}
//     </Card>
// );

// const ReportSingle = () => {
//     // const token = useSelector((state) => state.auth.user?.jwt);
//     // const user = useSelector((state) => state.auth.user);

//     const {
//         user,
//         accessToken: token,
//     } = useSelector((state) => state.auth);

//     const { id } = useParams();
//     const [report, setReport] = useState(null);
//     const viewRef = useRef();
//     const containerRef = useRef()

//     useEffect(() => {
//         if (!token) return;
//         const fetchReport = async () => {
//             try {
//                 const res = await getSingleReport(id, token);
//                 console.log(res?.reportData);
//                 setReport(res?.reportData);
//             } catch (err) {
//                 console.error("Błąd pobierania raportu:", err);
//             }
//         };
//         fetchReport();
//     }, [id, token]);


//     if (!report) return <Box sx={{
//         position: "absolute",
//         left: "50%",
//         top: "50%"
//     }}><CircularProgress /></Box>;

//     const generatePdf = async () => {
//         if (!containerRef.current) return;

//         const canvas = await html2canvas(containerRef.current, { scale: 2 });
//         // const canvas = await html2canvas(containerRef.current, {
//         //     scale: 1
//         // });
//         // const imgData = canvas.toDataURL("image/png");
//         const imgData = canvas.toDataURL("image/jpeg", 1);

//         // const pdf = new jsPDF("p", "mm", "a4");
//         const pdf = new jsPDF({
//             orientation: "p",
//             unit: "mm",
//             format: "a4",
//             compress: true
//         });
//         const pdfWidth = pdf.internal.pageSize.getWidth();
//         const pdfHeight = pdf.internal.pageSize.getHeight();

//         const imgProps = pdf.getImageProperties(imgData);
//         const imgWidth = imgProps.width;
//         const imgHeight = imgProps.height;

//         const scaledWidth = pdfWidth;
//         const scaledHeight = (imgHeight * pdfWidth) / imgWidth;

//         let position = 0;

//         if (scaledHeight <= pdfHeight) {
//             pdf.addImage(imgData, "PNG", 0, 0, scaledWidth, scaledHeight);
//         } else {
//             let remainingHeight = scaledHeight;
//             let y = 0;

//             while (remainingHeight > 0) {
//                 const pageHeight = Math.min(remainingHeight, pdfHeight);
//                 pdf.addImage(
//                     imgData,
//                     "PNG",
//                     0,
//                     -y,
//                     scaledWidth,
//                     scaledHeight
//                 );

//                 remainingHeight -= pdfHeight;
//                 y += pdfHeight;

//                 if (remainingHeight > 0) pdf.addPage();
//             }
//         }

//         pdf.save("raport.pdf");
//     };



//     return (
//         <Container
//             ref={containerRef}
//             maxWidth="xl"
//             sx={{
//                 mt: { xs: 2, md: 5 },
//                 mb: 6,
//             }}
//         >
//             <Box
//                 ref={viewRef}
//                 sx={{
//                     minHeight: "100vh",
//                     bgcolor: "#F4F7FB",
//                     borderRadius: { xs: 0, md: "28px" },
//                     p: { xs: 2, sm: 3, md: 4 },
//                 }}
//             >

//                 <Card
//                     elevation={0}
//                     sx={{
//                         position: "relative",
//                         overflow: "hidden",
//                         mb: 3,
//                         borderRadius: "24px",
//                         color: "#fff",
//                         background:
//                             "linear-gradient(135deg, #111827 0%, #283E70 55%, #4F46E5 100%)",
//                     }}
//                 >
//                     {/* dekoracja */}
//                     <Box
//                         sx={{
//                             position: "absolute",
//                             width: 350,
//                             height: 350,
//                             borderRadius: "50%",
//                             right: -120,
//                             top: -180,
//                             bgcolor: "rgba(255,255,255,0.08)",
//                         }}
//                     />

//                     <Box
//                         sx={{
//                             position: "absolute",
//                             width: 220,
//                             height: 220,
//                             borderRadius: "50%",
//                             right: 120,
//                             bottom: -170,
//                             bgcolor: "rgba(255,255,255,0.05)",
//                         }}
//                     />

//                     <Box
//                         sx={{
//                             position: "relative",
//                             zIndex: 1,
//                             p: { xs: 3, md: 4 },
//                             display: "flex",
//                             flexDirection: {
//                                 xs: "column",
//                                 md: "row",
//                             },
//                             alignItems: {
//                                 xs: "flex-start",
//                                 md: "center",
//                             },
//                             justifyContent: "space-between",
//                             gap: 3,
//                         }}
//                     >
//                         <Box>
//                             <Chip
//                                 label="RAPORT RODZICA"
//                                 sx={{
//                                     mb: 2,
//                                     bgcolor: "rgba(255,255,255,0.14)",
//                                     color: "#fff",
//                                     fontWeight: 700,
//                                     letterSpacing: ".08em",
//                                     fontSize: "0.72rem",
//                                     backdropFilter: "blur(10px)",
//                                 }}
//                             />

//                             <Typography
//                                 variant="h3"
//                                 sx={{
//                                     fontSize: {
//                                         xs: "1.8rem",
//                                         md: "2.5rem",
//                                     },
//                                     lineHeight: 1.15,
//                                     fontWeight: 800,
//                                     maxWidth: 700,
//                                 }}
//                             >
//                                 Ścieżka kompetencyjna
//                                 <Box
//                                     component="span"
//                                     sx={{
//                                         display: "block",
//                                         color: "#A5B4FC",
//                                     }}
//                                 >
//                                     Twojego dziecka
//                                 </Box>
//                             </Typography>

//                             <Typography
//                                 sx={{
//                                     mt: 2,
//                                     color: "rgba(255,255,255,.72)",
//                                     maxWidth: 620,
//                                 }}
//                             >
//                                 Postępy, zdobyte kompetencje, mikropoświadczenia
//                                 oraz rekomendacje rozwoju w jednym miejscu.
//                             </Typography>
//                         </Box>

//                         <Stack
//                             direction="row"
//                             spacing={2}
//                             alignItems="center"
//                             sx={{
//                                 bgcolor: "rgba(255,255,255,.10)",
//                                 border: "1px solid rgba(255,255,255,.15)",
//                                 px: 2.5,
//                                 py: 2,
//                                 borderRadius: "18px",
//                                 backdropFilter: "blur(12px)",
//                                 minWidth: { md: 260 },
//                             }}
//                         >
//                             <Avatar
//                                 sx={{
//                                     width: 58,
//                                     height: 58,
//                                     bgcolor: "#fff",
//                                     color: "#4F46E5",
//                                     fontSize: "1.45rem",
//                                     fontWeight: 800,
//                                 }}
//                             >
//                                 {report?.user?.name
//                                     ?.charAt(0)
//                                     ?.toUpperCase() ?? ""}
//                             </Avatar>

//                             <Box>
//                                 <Typography
//                                     variant="caption"
//                                     sx={{
//                                         color: "rgba(255,255,255,.65)",
//                                     }}
//                                 >
//                                     Profil dziecka
//                                 </Typography>

//                                 <Typography
//                                     variant="h6"
//                                     sx={{
//                                         fontWeight: 700,
//                                     }}
//                                 >
//                                     {report?.user?.name ?? ""}
//                                 </Typography>
//                             </Box>
//                         </Stack>
//                     </Box>
//                 </Card>


//                 <Box sx={{ mb: 3 }}>
//                     <Typography
//                         variant="h5"
//                         sx={{
//                             fontWeight: 800,
//                             color: "#111827",
//                             mb: 0.5,
//                         }}
//                     >
//                         Mikropoświadczenia
//                     </Typography>

//                     <Typography
//                         variant="body2"
//                         sx={{
//                             color: "#6B7280",
//                             mb: 2.5,
//                         }}
//                     >
//                         Podsumowanie zdobytych odznak i kwalifikacji.
//                     </Typography>

//                     <Grid container spacing={2.5}>

//                         <Grid item xs={12} md={4}>
//                             <Card
//                                 elevation={0}
//                                 sx={{
//                                     height: "100%",
//                                     borderRadius: "22px",
//                                     position: "relative",
//                                     overflow: "hidden",
//                                     border: "1px solid #F5E6B8",
//                                     background:
//                                         "linear-gradient(145deg, #FFFFFF 0%, #FFF9E8 100%)",
//                                     transition: ".25s ease",
//                                     "&:hover": {
//                                         transform: "translateY(-4px)",
//                                         boxShadow:
//                                             "0 16px 35px rgba(17,24,39,.08)",
//                                     },
//                                 }}
//                             >
//                                 <CardContent
//                                     sx={{
//                                         p: 3,
//                                         "&:last-child": { pb: 3 },
//                                     }}
//                                 >
//                                     <Stack
//                                         direction="row"
//                                         justifyContent="space-between"
//                                         alignItems="flex-start"
//                                     >
//                                         <Box>
//                                             <Typography
//                                                 variant="body2"
//                                                 sx={{
//                                                     color: "#8A6A16",
//                                                     fontWeight: 700,
//                                                 }}
//                                             >
//                                                 KWALIFIKACJE
//                                             </Typography>

//                                             <Typography
//                                                 variant="h3"
//                                                 sx={{
//                                                     mt: 1,
//                                                     fontWeight: 800,
//                                                     color: "#111827",
//                                                 }}
//                                             >
//                                                 {report?.earnedCredsGold ?? 0}
//                                             </Typography>
//                                         </Box>

//                                         <Box
//                                             sx={{
//                                                 width: 72,
//                                                 height: 72,
//                                                 borderRadius: "18px",
//                                                 bgcolor: "#FFF4C5",
//                                                 display: "grid",
//                                                 placeItems: "center",
//                                             }}
//                                         >
//                                             <CardMedia
//                                                 component="img"
//                                                 image="/assets/badge_gold.png"
//                                                 alt="Złote odznaki"
//                                                 sx={{
//                                                     width: 52,
//                                                     height: 52,
//                                                     objectFit: "contain",
//                                                 }}
//                                             />
//                                         </Box>
//                                     </Stack>

//                                     <Typography
//                                         variant="body2"
//                                         sx={{
//                                             mt: 2,
//                                             color: "#6B7280",
//                                         }}
//                                     >
//                                         Złote odznaki
//                                     </Typography>
//                                 </CardContent>
//                             </Card>
//                         </Grid>


//                         <Grid item xs={12} md={4}>
//                             <Card
//                                 elevation={0}
//                                 sx={{
//                                     height: "100%",
//                                     borderRadius: "22px",
//                                     overflow: "hidden",
//                                     border: "1px solid #E5E7EB",
//                                     background:
//                                         "linear-gradient(145deg, #FFFFFF 0%, #F3F4F6 100%)",
//                                     transition: ".25s ease",
//                                     "&:hover": {
//                                         transform: "translateY(-4px)",
//                                         boxShadow:
//                                             "0 16px 35px rgba(17,24,39,.08)",
//                                     },
//                                 }}
//                             >
//                                 <CardContent
//                                     sx={{
//                                         p: 3,
//                                         "&:last-child": { pb: 3 },
//                                     }}
//                                 >
//                                     <Stack
//                                         direction="row"
//                                         justifyContent="space-between"
//                                         alignItems="flex-start"
//                                     >
//                                         <Box>
//                                             <Typography
//                                                 variant="body2"
//                                                 sx={{
//                                                     color: "#6B7280",
//                                                     fontWeight: 700,
//                                                 }}
//                                             >
//                                                 OSIĄGNIĘCIA WŁASNE
//                                             </Typography>

//                                             <Typography
//                                                 variant="h3"
//                                                 sx={{
//                                                     mt: 1,
//                                                     fontWeight: 800,
//                                                     color: "#111827",
//                                                 }}
//                                             >
//                                                 {report?.earnedCredsSilver ?? 0}
//                                             </Typography>
//                                         </Box>

//                                         <Box
//                                             sx={{
//                                                 width: 72,
//                                                 height: 72,
//                                                 borderRadius: "18px",
//                                                 bgcolor: "#E5E7EB",
//                                                 display: "grid",
//                                                 placeItems: "center",
//                                             }}
//                                         >
//                                             <CardMedia
//                                                 component="img"
//                                                 image="/assets/badge_silver.png"
//                                                 alt="Srebrne odznaki"
//                                                 sx={{
//                                                     width: 52,
//                                                     height: 52,
//                                                     objectFit: "contain",
//                                                 }}
//                                             />
//                                         </Box>
//                                     </Stack>

//                                     <Typography
//                                         variant="body2"
//                                         sx={{
//                                             mt: 2,
//                                             color: "#6B7280",
//                                         }}
//                                     >
//                                         Srebrne odznaki
//                                     </Typography>
//                                 </CardContent>
//                             </Card>
//                         </Grid>


//                         <Grid item xs={12} md={4}>
//                             <Card
//                                 elevation={0}
//                                 sx={{
//                                     height: "100%",
//                                     borderRadius: "22px",
//                                     overflow: "hidden",
//                                     border: "1px solid #EAD5C2",
//                                     background:
//                                         "linear-gradient(145deg, #FFFFFF 0%, #FFF3EB 100%)",
//                                     transition: ".25s ease",
//                                     "&:hover": {
//                                         transform: "translateY(-4px)",
//                                         boxShadow:
//                                             "0 16px 35px rgba(17,24,39,.08)",
//                                     },
//                                 }}
//                             >
//                                 <CardContent
//                                     sx={{
//                                         p: 3,
//                                         "&:last-child": { pb: 3 },
//                                     }}
//                                 >
//                                     <Stack
//                                         direction="row"
//                                         justifyContent="space-between"
//                                         alignItems="flex-start"
//                                     >
//                                         <Box>
//                                             <Typography
//                                                 variant="body2"
//                                                 sx={{
//                                                     color: "#9A5A2A",
//                                                     fontWeight: 700,
//                                                 }}
//                                             >
//                                                 VR
//                                             </Typography>

//                                             <Typography
//                                                 variant="h3"
//                                                 sx={{
//                                                     mt: 1,
//                                                     fontWeight: 800,
//                                                     color: "#111827",
//                                                 }}
//                                             >
//                                                 {report?.earnedCredsBronze ?? 0}
//                                             </Typography>
//                                         </Box>

//                                         <Box
//                                             sx={{
//                                                 width: 72,
//                                                 height: 72,
//                                                 borderRadius: "18px",
//                                                 bgcolor: "#F7E2D3",
//                                                 display: "grid",
//                                                 placeItems: "center",
//                                             }}
//                                         >
//                                             <CardMedia
//                                                 component="img"
//                                                 image="/assets/badge_brown.png"
//                                                 alt="Brązowe odznaki"
//                                                 sx={{
//                                                     width: 52,
//                                                     height: 52,
//                                                     objectFit: "contain",
//                                                 }}
//                                             />
//                                         </Box>
//                                     </Stack>

//                                     <Typography
//                                         variant="body2"
//                                         sx={{
//                                             mt: 2,
//                                             color: "#6B7280",
//                                         }}
//                                     >
//                                         Brązowe odznaki
//                                     </Typography>
//                                 </CardContent>
//                             </Card>
//                         </Grid>
//                     </Grid>
//                 </Box>


//                 <Card
//                     elevation={0}
//                     sx={{
//                         mb: 3,
//                         borderRadius: "22px",
//                         border: "1px solid #E8ECF2",
//                         bgcolor: "#fff",
//                     }}
//                 >
//                     <Box
//                         sx={{
//                             px: { xs: 2, md: 3 },
//                             pt: 3,
//                             pb: 2,
//                         }}
//                     >
//                         <Stack
//                             direction="row"
//                             spacing={1.5}
//                             alignItems="center"
//                         >
//                             <Box
//                                 sx={{
//                                     width: 42,
//                                     height: 42,
//                                     borderRadius: "12px",
//                                     bgcolor: "#EEF2FF",
//                                     color: "#4F46E5",
//                                     display: "grid",
//                                     placeItems: "center",
//                                 }}
//                             >
//                                 <WorkspacePremiumRoundedIcon />
//                             </Box>

//                             <Box>
//                                 <Typography
//                                     variant="h6"
//                                     sx={{ fontWeight: 800 }}
//                                 >
//                                     Szczegóły odznak
//                                 </Typography>

//                                 <Typography
//                                     variant="body2"
//                                     color="text.secondary"
//                                 >
//                                     Zdobyte mikropoświadczenia i ich status.
//                                 </Typography>
//                             </Box>
//                         </Stack>
//                     </Box>

//                     <Divider />

//                     <Stack divider={<Divider />}>
//                         {report?.badges?.map((badge) => (
//                             <Box
//                                 key={badge.id}
//                                 sx={{
//                                     p: { xs: 2, md: 2.5 },
//                                     display: "flex",
//                                     flexDirection: {
//                                         xs: "column",
//                                         sm: "row",
//                                     },
//                                     gap: 2,
//                                     transition: ".2s",
//                                     "&:hover": {
//                                         bgcolor: "#F9FAFB",
//                                     },
//                                 }}
//                             >
//                                 <Box
//                                     sx={{
//                                         width: {
//                                             xs: "100%",
//                                             sm: 76,
//                                         },
//                                         minWidth: 76,
//                                         height: 76,
//                                         borderRadius: "16px",
//                                         bgcolor: "#F4F7FB",
//                                         display: "grid",
//                                         placeItems: "center",
//                                     }}
//                                 >
//                                     <CardMedia
//                                         component="img"
//                                         image={badge.image?.url}
//                                         alt={badge.badgeName}
//                                         sx={{
//                                             width: 56,
//                                             height: 62,
//                                             objectFit: "contain",
//                                         }}
//                                     />
//                                 </Box>

//                                 <Box
//                                     sx={{
//                                         flex: 1,
//                                         minWidth: 0,
//                                     }}
//                                 >
//                                     <Stack
//                                         direction={{
//                                             xs: "column",
//                                             sm: "row",
//                                         }}
//                                         alignItems={{
//                                             xs: "flex-start",
//                                             sm: "center",
//                                         }}
//                                         justifyContent="space-between"
//                                         gap={1}
//                                     >
//                                         <Typography
//                                             variant="subtitle1"
//                                             sx={{
//                                                 fontWeight: 800,
//                                                 color: "#111827",
//                                             }}
//                                         >
//                                             {badge.badgeName}
//                                         </Typography>

//                                         <Stack
//                                             direction="row"
//                                             spacing={1}
//                                             flexWrap="wrap"
//                                             useFlexGap
//                                         >
//                                             {badge.badgeType && (
//                                                 <Chip
//                                                     size="small"
//                                                     label={badge.badgeType}
//                                                     sx={{
//                                                         bgcolor: "#EEF2FF",
//                                                         color: "#4338CA",
//                                                         fontWeight: 600,
//                                                     }}
//                                                 />
//                                             )}

//                                             {badge.status && (
//                                                 <Chip
//                                                     size="small"
//                                                     label={badge.status}
//                                                     sx={{
//                                                         bgcolor: "#ECFDF3",
//                                                         color: "#15803D",
//                                                         fontWeight: 600,
//                                                     }}
//                                                 />
//                                             )}
//                                         </Stack>
//                                     </Stack>

//                                     {badge.qualificationName && (
//                                         <Typography
//                                             variant="body2"
//                                             sx={{
//                                                 mt: 0.75,
//                                                 color: "#4B5563",
//                                             }}
//                                         >
//                                             {badge.qualificationName}
//                                         </Typography>
//                                     )}

//                                     <Stack
//                                         direction="row"
//                                         spacing={2}
//                                         flexWrap="wrap"
//                                         useFlexGap
//                                         sx={{ mt: 1.3 }}
//                                     >
//                                         {badge.prkLevel && (
//                                             <Typography
//                                                 variant="caption"
//                                                 color="text.secondary"
//                                             >
//                                                 Poziom PRK:{" "}
//                                                 <strong>
//                                                     {badge.prkLevel}
//                                                 </strong>
//                                             </Typography>
//                                         )}

//                                         {badge.expirationDate && (
//                                             <Typography
//                                                 variant="caption"
//                                                 color="text.secondary"
//                                             >
//                                                 Wygasa:{" "}
//                                                 <strong>
//                                                     {new Date(
//                                                         badge.expirationDate
//                                                     ).toLocaleDateString()}
//                                                 </strong>
//                                             </Typography>
//                                         )}
//                                     </Stack>
//                                 </Box>
//                             </Box>
//                         ))}
//                     </Stack>
//                 </Card>


//                 <Grid container spacing={3}>

//                     <Grid item xs={12} lg={8}>
//                         <Stack spacing={3}>

//                             <Card
//                                 elevation={0}
//                                 sx={{
//                                     borderRadius: "22px",
//                                     border: "1px solid #E8ECF2",
//                                     p: { xs: 2, md: 3 },
//                                 }}
//                             >
//                                 <Stack
//                                     direction="row"
//                                     spacing={1.5}
//                                     alignItems="center"
//                                     mb={3}
//                                 >
//                                     <Box
//                                         sx={{
//                                             width: 42,
//                                             height: 42,
//                                             borderRadius: "12px",
//                                             bgcolor: "#ECFDF5",
//                                             color: "#059669",
//                                             display: "grid",
//                                             placeItems: "center",
//                                         }}
//                                     >
//                                         <SchoolRoundedIcon />
//                                     </Box>

//                                     <Box>
//                                         <Typography
//                                             variant="h6"
//                                             fontWeight={800}
//                                         >
//                                             Efekty uczenia się
//                                         </Typography>

//                                         <Typography
//                                             variant="body2"
//                                             color="text.secondary"
//                                         >
//                                             Umiejętności i kompetencje
//                                             Twojego dziecka.
//                                         </Typography>
//                                     </Box>
//                                 </Stack>

//                                 <SkillsSummaryChart
//                                     skills={report.skillsSumarize}
//                                 />
//                             </Card>


//                             <Card
//                                 elevation={0}
//                                 sx={{
//                                     borderRadius: "22px",
//                                     border: "1px solid #E8ECF2",
//                                     p: { xs: 2, md: 3 },
//                                 }}
//                             >
//                                 <Stack
//                                     direction="row"
//                                     spacing={1.5}
//                                     alignItems="center"
//                                     mb={3}
//                                 >
//                                     <Box
//                                         sx={{
//                                             width: 42,
//                                             height: 42,
//                                             borderRadius: "12px",
//                                             bgcolor: "#EEF2FF",
//                                             color: "#4F46E5",
//                                             display: "grid",
//                                             placeItems: "center",
//                                         }}
//                                     >
//                                         <InsightsRoundedIcon />
//                                     </Box>

//                                     <Box>
//                                         <Typography
//                                             variant="h6"
//                                             fontWeight={800}
//                                         >
//                                             Na tle innych użytkowników
//                                         </Typography>

//                                         <Typography
//                                             variant="body2"
//                                             color="text.secondary"
//                                         >
//                                             Porównanie wyników i aktywności
//                                             użytkowników micree.
//                                         </Typography>
//                                     </Box>
//                                 </Stack>

//                                 {report.comparison ? (
//                                     <ComparisonBarChart
//                                         comparison={report?.comparison}
//                                     />
//                                 ) : (
//                                     <Box
//                                         sx={{
//                                             bgcolor: "#F9FAFB",
//                                             borderRadius: "16px",
//                                             py: 6,
//                                             textAlign: "center",
//                                         }}
//                                     >
//                                         <Typography color="text.secondary">
//                                             Brak danych porównawczych
//                                         </Typography>
//                                     </Box>
//                                 )}
//                             </Card>
//                         </Stack>
//                     </Grid>


//                     <Grid item xs={12} lg={4}>
//                         <Stack spacing={3}>
//                             <Card
//                                 elevation={0}
//                                 sx={{
//                                     borderRadius: "22px",
//                                     border: "1px solid #E8ECF2",
//                                     p: { xs: 2, md: 3 },
//                                 }}
//                             >
//                                 <Stack
//                                     direction="row"
//                                     spacing={1.5}
//                                     alignItems="center"
//                                     mb={3}
//                                 >
//                                     <Box
//                                         sx={{
//                                             width: 42,
//                                             height: 42,
//                                             borderRadius: "12px",
//                                             bgcolor: "#FFF7ED",
//                                             color: "#EA580C",
//                                             display: "grid",
//                                             placeItems: "center",
//                                         }}
//                                     >
//                                         <TrendingUpRoundedIcon />
//                                     </Box>

//                                     <Box>
//                                         <Typography
//                                             variant="h6"
//                                             fontWeight={800}
//                                         >
//                                             Popularne kwalifikacje
//                                         </Typography>

//                                         <Typography
//                                             variant="body2"
//                                             color="text.secondary"
//                                         >
//                                             Co wybierają inni.
//                                         </Typography>
//                                     </Box>
//                                 </Stack>

//                                 <PopularQualifications
//                                     popularQualifications={
//                                         report?.popularQualifications
//                                     }
//                                 />
//                             </Card>

//                             {/* sugestie */}

//                             <Card
//                                 elevation={0}
//                                 sx={{
//                                     borderRadius: "22px",
//                                     border: "1px solid #E8ECF2",
//                                     p: { xs: 2, md: 3 },
//                                 }}
//                             >
//                                 <Typography
//                                     variant="h6"
//                                     sx={{
//                                         fontWeight: 800,
//                                         mb: 0.5,
//                                     }}
//                                 >
//                                     Sugestie rodzica
//                                 </Typography>

//                                 <Typography
//                                     variant="body2"
//                                     color="text.secondary"
//                                     sx={{ mb: 2.5 }}
//                                 >
//                                     Dodaj własne sugestie dotyczące
//                                     dalszego rozwoju dziecka.
//                                 </Typography>

//                                 <ChildDevelopmentSuggestions
//                                     reportId={id}
//                                     parentSuggestions={
//                                         report?.parentSuggestions
//                                     }
//                                 />
//                             </Card>
//                         </Stack>
//                     </Grid>
//                 </Grid>


//                 <Box sx={{ mt: 3 }}>
//                     <Stack
//                         direction="row"
//                         spacing={1.5}
//                         alignItems="center"
//                         mb={2}
//                     >
//                         <Box
//                             sx={{
//                                 width: 42,
//                                 height: 42,
//                                 borderRadius: "12px",
//                                 bgcolor: "#F3E8FF",
//                                 color: "#7E22CE",
//                                 display: "grid",
//                                 placeItems: "center",
//                             }}
//                         >
//                             <CalendarMonthRoundedIcon />
//                         </Box>

//                         <Box>
//                             <Typography variant="h6" fontWeight={800}>
//                                 Aktywność i wydarzenia
//                             </Typography>

//                             <Typography
//                                 variant="body2"
//                                 color="text.secondary"
//                             >
//                                 Historia oraz planowane aktywności.
//                             </Typography>
//                         </Box>
//                     </Stack>

//                     <Grid container spacing={3}>
//                         <Grid item xs={12} md={6}>
//                             <PastEventsCard
//                                 user={user}
//                                 report={report}
//                             />
//                         </Grid>

//                         <Grid item xs={12} md={6}>
//                             <UpcomingEventsCard user={user} />
//                         </Grid>
//                     </Grid>
//                 </Box>

//                 <Card
//                     elevation={0}
//                     sx={{
//                         mt: 3,
//                         borderRadius: "24px",
//                         overflow: "hidden",
//                         border: "1px solid #DDD6FE",
//                         background:
//                             "linear-gradient(135deg, #F5F3FF 0%, #EEF2FF 100%)",
//                     }}
//                 >
//                     <Box
//                         sx={{
//                             px: { xs: 2, md: 3 },
//                             pt: 3,
//                             pb: 1,
//                         }}
//                     >
//                         <Stack
//                             direction="row"
//                             spacing={1.5}
//                             alignItems="center"
//                         >
//                             <Box
//                                 sx={{
//                                     width: 46,
//                                     height: 46,
//                                     borderRadius: "14px",
//                                     bgcolor: "#4F46E5",
//                                     color: "#fff",
//                                     display: "grid",
//                                     placeItems: "center",
//                                     boxShadow:
//                                         "0 8px 20px rgba(79,70,229,.25)",
//                                 }}
//                             >
//                                 <AutoAwesomeRoundedIcon />
//                             </Box>

//                             <Box>
//                                 <Typography
//                                     variant="h6"
//                                     fontWeight={800}
//                                 >
//                                     Doradca rozwoju AI
//                                 </Typography>

//                                 <Typography
//                                     variant="body2"
//                                     color="text.secondary"
//                                 >
//                                     Rekomendacje na podstawie kompetencji,
//                                     osiągnięć i aktywności.
//                                 </Typography>
//                             </Box>
//                         </Stack>
//                     </Box>

//                     <Box sx={{ p: { xs: 2, md: 3 } }}>
//                         <CareerAIAdvisorCard
//                             studentData={report}
//                         />
//                     </Box>
//                 </Card>


//                 <Box
//                     sx={{
//                         mt: 4,
//                         p: { xs: 2.5, md: 3 },
//                         bgcolor: "#fff",
//                         border: "1px solid #E8ECF2",
//                         borderRadius: "22px",
//                         display: "flex",
//                         flexDirection: {
//                             xs: "column",
//                             sm: "row",
//                         },
//                         justifyContent: "space-between",
//                         alignItems: {
//                             xs: "stretch",
//                             sm: "center",
//                         },
//                         gap: 2,
//                     }}
//                 >
//                     <Box>
//                         <Typography
//                             variant="subtitle1"
//                             sx={{
//                                 fontWeight: 800,
//                                 color: "#111827",
//                             }}
//                         >
//                             Pełny raport kompetencyjny
//                         </Typography>

//                         <Typography
//                             variant="body2"
//                             color="text.secondary"
//                         >
//                             Pobierz raport i zachowaj go w formacie PDF.
//                         </Typography>
//                     </Box>

//                     <Button
//                         variant="contained"
//                         size="large"
//                         onClick={generatePdf}
//                         startIcon={<DownloadRoundedIcon />}
//                         sx={{
//                             borderRadius: "14px",
//                             textTransform: "none",
//                             fontWeight: 700,
//                             px: 3,
//                             py: 1.3,
//                             minWidth: 190,
//                             bgcolor: "#4F46E5",
//                             boxShadow:
//                                 "0 8px 20px rgba(79,70,229,.22)",
//                             "&:hover": {
//                                 bgcolor: "#4338CA",
//                                 boxShadow:
//                                     "0 10px 25px rgba(79,70,229,.3)",
//                             },
//                         }}
//                     >
//                         Pobierz PDF
//                     </Button>
//                 </Box>
//             </Box>
//         </Container>

//     );
// };

// export default ReportSingle;






// import React, { useEffect, useRef, useState } from "react";
// import {
//     Box,
//     Typography,
//     Card,
//     CardContent,
//     CardMedia,
//     Stack,
//     Button,
//     Grid,
//     Fab,
//     CircularProgress,
//     Container,
//     Avatar
// } from "@mui/material";
// import DownloadIcon from "@mui/icons-material/Download";
// import { useParams } from "react-router-dom";
// import { useSelector } from "react-redux";
// import { getSingleReport } from "../../Slices/reports";
// import StudentSkillProfileChartWeb from "../../Components/reports/RadarCompetencyChart";
// import UpcomingEventsCard from "../../Components/reports/UpcomingEvents";
// import SkillsSummaryChart from "../../Components/reports/SkillsSummaryChart";
// import ComparisonBarChart from "../../Components/reports/ComparisonBarChart";
// import PopularQualifications from "../../Components/reports/PopularQualifications";
// import ChildDevelopmentSuggestions from "../../Components/reports/ChildDevelopmentSuggestions";
// import jsPDF from "jspdf";
// import html2canvas from "html2canvas";
// import PastEventsCard from "../../Components/reports/PastEvents";
// import CareerAIAdvisorCard from "../../Components/reports/AIAdvice";


// const GradientCard = ({ gradient, children }) => (
//     <Card sx={{ background: gradient, color: "#fff", borderRadius: 12 }}>
//         {children}
//     </Card>
// );

// const ReportSingle = () => {
//     // const token = useSelector((state) => state.auth.user?.jwt);
//     // const user = useSelector((state) => state.auth.user);

//     const {
//         user,
//         accessToken: token,
//     } = useSelector((state) => state.auth);

//     const { id } = useParams();
//     const [report, setReport] = useState(null);
//     const viewRef = useRef();
//     const containerRef = useRef()

//     useEffect(() => {
//         if (!token) return;
//         const fetchReport = async () => {
//             try {
//                 const res = await getSingleReport(id, token);
//                 console.log(res?.reportData);
//                 setReport(res?.reportData);
//             } catch (err) {
//                 console.error("Błąd pobierania raportu:", err);
//             }
//         };
//         fetchReport();
//     }, [id, token]);


//     if (!report) return <Box sx={{
//         position: "absolute",
//         left: "50%",
//         top: "50%"
//     }}><CircularProgress /></Box>;

//     const generatePdf = async () => {
//         if (!containerRef.current) return;

//         const canvas = await html2canvas(containerRef.current, { scale: 2 });
//         // const canvas = await html2canvas(containerRef.current, {
//         //     scale: 1
//         // });
//         // const imgData = canvas.toDataURL("image/png");
//         const imgData = canvas.toDataURL("image/jpeg", 1);

//         // const pdf = new jsPDF("p", "mm", "a4");
//         const pdf = new jsPDF({
//             orientation: "p",
//             unit: "mm",
//             format: "a4",
//             compress: true
//         });
//         const pdfWidth = pdf.internal.pageSize.getWidth();
//         const pdfHeight = pdf.internal.pageSize.getHeight();

//         const imgProps = pdf.getImageProperties(imgData);
//         const imgWidth = imgProps.width;
//         const imgHeight = imgProps.height;

//         const scaledWidth = pdfWidth;
//         const scaledHeight = (imgHeight * pdfWidth) / imgWidth;

//         let position = 0;

//         if (scaledHeight <= pdfHeight) {
//             pdf.addImage(imgData, "PNG", 0, 0, scaledWidth, scaledHeight);
//         } else {
//             let remainingHeight = scaledHeight;
//             let y = 0;

//             while (remainingHeight > 0) {
//                 const pageHeight = Math.min(remainingHeight, pdfHeight);
//                 pdf.addImage(
//                     imgData,
//                     "PNG",
//                     0,
//                     -y,
//                     scaledWidth,
//                     scaledHeight
//                 );

//                 remainingHeight -= pdfHeight;
//                 y += pdfHeight;

//                 if (remainingHeight > 0) pdf.addPage();
//             }
//         }

//         pdf.save("raport.pdf");
//     };



//     return (
//         <Container ref={containerRef} maxWidth="lg" sx={{ mt: 8 }}>
//             <Box ref={viewRef} sx={{ p: 4, bgcolor: "#f7f7f7", minHeight: "100vh", borderRadius: '18px' }}>
//                 {/* <Typography variant="h4" mb={3}>
//                 RAPORT RODZICA
//             </Typography> */}

//                 <Typography variant="h4" mb={3} sx={{ justifySelf: 'center' }}>Ścieżka kompetencyjna Twojego dziecka</Typography>

//                 <Card sx={{ mb: 3, p: 2, borderRadius: '18px' }}>

//                     <Box
//                         sx={{
//                             display: "flex",
//                             flexDirection: "column",
//                             alignItems: "center",
//                             mb: 6,
//                         }}
//                     >
//                         {/* <Avatar
//                             sx={{
//                                 width: 80,
//                                 height: 80,
//                                 mb: 2,
//                             }}
//                         >
//                             {report?.user?.name?.charAt(0)?.toUpperCase() ?? ""}
//                         </Avatar> */}

//                         <Typography variant="h6">
//                             {report?.user?.name ?? ""}
//                         </Typography>
//                     </Box>

//                     <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>Mikropoświadczenia Twojego dziecka</Typography>


//                     <Grid container spacing={2}>
//                         <Grid item xs={12} sm={4}>
//                             <GradientCard gradient="linear-gradient(135deg, #FFD700, #FFA500)">
//                                 <CardContent sx={{ textAlign: "center", py: 4 }}>
//                                     <Typography variant="subtitle1">Złote odznaki (Kwalifikacje)</Typography>
//                                     <CardMedia
//                                         component="img"
//                                         image="/assets/badge_gold.png"
//                                         alt="Złote odznaki"
//                                         sx={{ width: 80, height: 80, mx: "auto", mt: 2 }}
//                                     />
//                                     <Typography variant="h3" sx={{ mt: 1, fontWeight: 700 }}>
//                                         {report?.earnedCredsGold ?? 0}
//                                     </Typography>
//                                 </CardContent>
//                             </GradientCard>
//                         </Grid>

//                         <Grid item xs={12} sm={4}>
//                             <GradientCard gradient="linear-gradient(135deg, #C0C0C0, #A9A9A9)">
//                                 <CardContent sx={{ textAlign: "center", py: 4 }}>
//                                     <Typography variant="subtitle1">Srebrne odznaki – osiągnięcia własne</Typography>
//                                     <CardMedia
//                                         component="img"
//                                         image="/assets/badge_silver.png"
//                                         alt="Srebrne odznaki"
//                                         sx={{ width: 80, height: 80, mx: "auto", mt: 2 }}
//                                     />
//                                     <Typography variant="h3" sx={{ mt: 1, fontWeight: 700 }}>
//                                         {report?.earnedCredsSilver ?? 0}
//                                     </Typography>
//                                 </CardContent>
//                             </GradientCard>
//                         </Grid>

//                         <Grid item xs={12} sm={4}>
//                             <GradientCard gradient="linear-gradient(135deg, #CD7F32, #8B4513)">
//                                 <CardContent sx={{ textAlign: "center", py: 4 }}>
//                                     <Typography variant="subtitle1">Brązowe odznaki – VR</Typography>
//                                     <CardMedia
//                                         component="img"
//                                         image="/assets/badge_brown.png"
//                                         alt="Brązowe odznaki"
//                                         sx={{ width: 80, height: 80, mx: "auto", mt: 2 }}
//                                     />
//                                     <Typography variant="h3" sx={{ mt: 1, fontWeight: 700 }}>
//                                         {report?.earnedCredsBronze ?? 0}
//                                     </Typography>
//                                 </CardContent>
//                             </GradientCard>
//                         </Grid>
//                     </Grid>


//                     <Typography variant="h6" mb={2} mt={4}>Szczegóły odznak</Typography>
//                     <Stack spacing={2}>
//                         {report?.badges?.map((badge) => (
//                             <Card elevation={0} key={badge.id} sx={{ p: 2, display: "flex", gap: 2, borderBottom: '2px solid #e3e3e3ff', borderRadius: 0 }}>
//                                 <CardMedia
//                                     component="img"
//                                     src={badge.image?.url}
//                                     alt={badge.badgeName}
//                                     sx={{ width: 60, height: 66, borderRadius: 1 }}
//                                 />
//                                 <Box sx={{ flex: 1 }}>
//                                     <Typography variant="subtitle1" fontWeight={700}>{badge.badgeName}</Typography>
//                                     <Typography variant="body2" color="text.secondary">
//                                         Typ: {badge.badgeType ?? "—"} | Status: {badge.status ?? "—"}
//                                     </Typography>
//                                     {badge.prkLevel && (
//                                         <Typography variant="body2" color="text.secondary">Poziom PRK: {badge.prkLevel}</Typography>
//                                     )}
//                                     {badge.qualificationName && (
//                                         <Typography variant="body2" color="text.secondary">Kwalifikacja: {badge.qualificationName}</Typography>
//                                     )}
//                                     {badge.expirationDate && (
//                                         <Typography variant="body2" color="text.secondary">
//                                             Wygasa: {new Date(badge.expirationDate).toLocaleDateString()}
//                                         </Typography>
//                                     )}
//                                 </Box>
//                             </Card>
//                         ))}
//                     </Stack>

//                 </Card>


//                 <Card sx={{ mb: 3, p: 2, borderRadius: '18px' }}>
//                     <Typography variant="h6">Efekty uczenia się (umiejętności) Twojego dziecka</Typography>
//                     <SkillsSummaryChart skills={report.skillsSumarize} />
//                 </Card>
//                 {/* <Card sx={{ mb: 3, p: 2 }}>
//                 <Typography variant="h6">Efekty uczenia się (umiejętności) Twojego dziecka</Typography>
//                 <Typography variant="subtitle2">Twarde:</Typography>
//                 <Typography>{report.skillsSumarize?.hardSkills ?? "Brak danych"}</Typography>
//                 <Typography variant="subtitle2" mt={1}>Miękkie:</Typography>
//                 <Typography>{report.skillsSumarize?.softSkills ?? "Brak danych"}</Typography>
//             </Card> */}

//                 <Card sx={{ mb: 3, p: 2, borderRadius: '18px' }}>
//                     <Typography variant="h6">Twoje dziecko na tle innych użytkowników micree</Typography>
//                     {report.comparison ? (
//                         // <>
//                         //     <Typography>Liczba zdobytych odznak: {report.comparison.userTotal}</Typography>
//                         //     <Typography>Średnia innych użytkowników: {report.comparison.average}</Typography>
//                         // </>
//                         <ComparisonBarChart comparison={report?.comparison} />
//                     ) : (
//                         <Typography>Brak danych</Typography>
//                     )}
//                 </Card>

//                 <Card sx={{ mb: 3, p: 2, borderRadius: '18px' }}>
//                     <Typography variant="h6">
//                         Kwalifikacje wybierane przez innych użytkowników micree
//                     </Typography>
//                     {/* <Stack spacing={1}>
//                         {report.popularQualifications?.map((qual) => (
//                             <Typography key={qual.id}>• {qual.name}</Typography>
//                         )) || <Typography>Brak danych</Typography>}
//                     </Stack> */}
//                     <PopularQualifications popularQualifications={report?.popularQualifications} />
//                 </Card>


//                 <PastEventsCard user={user} report={report} />
//                 <UpcomingEventsCard user={user} />
//                 {/* <Card sx={{ mb: 3, p: 2 }}>
//                 <Typography variant="h6">
//                     Wydarzenia, w których może wziąć udział Twoje dziecko w najbliższym czasie
//                 </Typography>
//                 <Stack spacing={1}>
//                     {report.upcomingEvents?.map((event) => (
//                         <Typography key={event.id}>• {event.name} ({event.date})</Typography>
//                     )) || <Typography>Brak danych</Typography>}
//                 </Stack>
//             </Card> */}

//                 <Card sx={{ mb: 3, p: 2, borderRadius: '18px' }}>
//                     <Typography variant="h6">Sugestie rodzica</Typography>
//                     <Typography variant="subtitle2">Dodaj sugestie rozwoju Twojego dziecka</Typography>
//                     {/* <Typography>{report.advice ?? "Brak sugestii"}</Typography> */}
//                     <ChildDevelopmentSuggestions reportId={id} parentSuggestions={report?.parentSuggestions} />
//                 </Card>



//                 {/* <Card sx={{ mt: 4, p: 2, borderRadius: '18px' }}>
//                     <Typography variant="h6" mb={1}>Kursy i szkolenia</Typography>
//                     <Typography>{report?.coursesBadges ?? "Brak danych"}</Typography>
//                 </Card> */}

//                 {/* <Box sx={{ mt: 4 }}>
//                 <StudentSkillProfileChartWeb skills={report?.badges} />
//             </Box> */}

//                 <Box sx={{ mt: 4 }}>
//                     <CareerAIAdvisorCard studentData={report} />
//                 </Box>



//                 <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
//                     <Fab color="primary" onClick={generatePdf}><DownloadIcon /></Fab>
//                 </Box>
//                 <Typography variant="body2" align="center" color="text.secondary" mb={4} onClick={generatePdf}>
//                     Pobierz raport w formacie PDF
//                 </Typography>

//                 {/* <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
//                     <Button variant="contained" color="primary">
//                         Zobacz raport po 10 kwietnia
//                     </Button>
//                 </Box> */}
//             </Box>
//         </Container>
//     );
// };

// export default ReportSingle;



// import React, { useEffect, useRef, useState } from "react";
// import {
//     Box,
//     Typography,
//     Card,
//     CardContent,
//     CardMedia,
//     Stack,
//     Chip,
//     Button,
//     Grid,
//     Fab,
//     Paper
// } from "@mui/material";
// import DownloadIcon from "@mui/icons-material/Download";
// import { LinearProgress } from "@mui/material";
// import { styled } from "@mui/material/styles";
// // import html2pdf from "html2pdf.js";

// // import ReportsChart from "../../Components/reports/ReportsChart";
// // import SkillSummary from "../../Components/reports/SkillSummary";
// // import CareerAIAdvisorCard from "../../Components/reports/AIAdvice";
// import StudentSkillProfileChartWeb from "../../Components/reports/RadarCompetencyChart";
// import { useSelector } from "react-redux";
// import { getSingleReport } from "../../Slices/reports";
// import { useParams } from "react-router-dom";

// const GradientCard = styled(Card)(({ theme, gradient }) => ({
//     borderRadius: 12,
//     background: gradient,
//     color: "#fff",
//     position: "relative",
//     overflow: "hidden",
// }));

// const ReportSingle = () => {
//     const token = useSelector((state) => state.auth.user?.jwt);

//     const [report, setReport] = useState([]);

//     const { id } = useParams();
//     console.log('id', id)

//     useEffect(() => {
//         if (!token) return;

//         const fetchReports = async () => {
//             try {
//                 const res = await getSingleReport(id, token);
//                 setReport(res);
//             } catch (error) {
//                 console.error("Błąd pobierania raportów:", error);
//             }
//         };
//         fetchReports();
//     }, [token, id]);

//     console.log('report', report)

//     // const reportData = data?.reportData;
//     const viewRef = useRef();

//     // console.log('datadsadsa', data)

//     const generatePdf = () => {
//         const element = viewRef.current;
//         // html2pdf()
//         //     .from(element)
//         //     .set({ margin: 10, filename: "raport.pdf", html2canvas: { scale: 2 } })
//         //     .save();
//     };

//     return (
//         <Box
//             ref={viewRef}
//             sx={{
//                 p: { xs: 2, md: 4 },
//                 bgcolor: "#f7f7f7",
//                 minHeight: "100vh",
//             }}
//         >
//             {/* <SkillSummary reportData={reportData} /> */}

//             <Typography variant="h6" fontWeight={600} mt={4} mb={2}>
//                 Zdobyte odznaki
//             </Typography>
//             <Grid container spacing={2}>
//                 <Grid item xs={12} sm={4}>
//                     <GradientCard gradient="linear-gradient(135deg, #FFD700, #FFA500)">
//                         <CardContent sx={{ textAlign: "center", py: 4 }}>
//                             <Typography variant="subtitle1">
//                                 Złote odznaki (Kwalifikacje)
//                             </Typography>
//                             <CardMedia
//                                 component="img"
//                                 image="/assets/badge_gold.png"
//                                 alt="Złote odznaki"
//                                 sx={{ width: 80, height: 80, mx: "auto", mt: 2 }}
//                             />
//                             <Typography
//                                 variant="h3"
//                                 sx={{
//                                     mt: 1,
//                                     fontWeight: 700,
//                                     textShadow: "0px 2px 5px rgba(0,0,0,0.3)",
//                                 }}
//                             >
//                                 {report?.earnedCredsGold ?? 0}
//                             </Typography>
//                         </CardContent>
//                     </GradientCard>
//                 </Grid>

//                 <Grid item xs={12} sm={4}>
//                     <GradientCard gradient="linear-gradient(135deg, #C0C0C0, #A9A9A9)">
//                         <CardContent sx={{ textAlign: "center", py: 4 }}>
//                             <Typography variant="subtitle1">
//                                 Srebrne odznaki – osiągnięcia własne
//                             </Typography>
//                             <CardMedia
//                                 component="img"
//                                 image="/assets/badge_silver.png"
//                                 alt="Srebrne odznaki"
//                                 sx={{ width: 80, height: 80, mx: "auto", mt: 2 }}
//                             />
//                             <Typography
//                                 variant="h3"
//                                 sx={{
//                                     mt: 1,
//                                     fontWeight: 700,
//                                     textShadow: "0px 2px 5px rgba(0,0,0,0.3)",
//                                 }}
//                             >
//                                 {report?.earnedCredsSilver ?? 0}
//                             </Typography>
//                         </CardContent>
//                     </GradientCard>
//                 </Grid>

//                 <Grid item xs={12} sm={4}>
//                     <GradientCard gradient="linear-gradient(135deg, #CD7F32, #8B4513)">
//                         <CardContent sx={{ textAlign: "center", py: 4 }}>
//                             <Typography variant="subtitle1">
//                                 Brązowe odznaki – VR
//                             </Typography>
//                             <CardMedia
//                                 component="img"
//                                 image="/assets/badge_brown.png"
//                                 alt="Brązowe odznaki"
//                                 sx={{ width: 80, height: 80, mx: "auto", mt: 2 }}
//                             />
//                             <Typography
//                                 variant="h3"
//                                 sx={{
//                                     mt: 1,
//                                     fontWeight: 700,
//                                     textShadow: "0px 2px 5px rgba(0,0,0,0.3)",
//                                 }}
//                             >
//                                 {report?.earnedCredsBronze ?? 0}
//                             </Typography>
//                         </CardContent>
//                     </GradientCard>
//                 </Grid>
//             </Grid>

//             {/* Kursy */}
//             <Card sx={{ mt: 4, p: 2 }}>
//                 <Typography variant="h6" mb={1}>
//                     Kursy i szkolenia
//                 </Typography>
//                 <Typography>
//                     {report?.coursesBadges ?? "Brak danych"}
//                 </Typography>
//             </Card>

//             {/* Wykresy */}
//             <Box sx={{ mt: 4 }}>
//                 {/* <ReportsChart /> */}
//             </Box>

//             {/* <Box sx={{ mt: 4 }}>
//                 <StudentSkillProfileChartWeb skills={report?.badges} />
//             </Box> */}

//             {/* Lista szczegółowa odznak */}
//             <Card sx={{ mt: 4, p: 2 }}>
//                 <Typography variant="h6" mb={2}>
//                     Szczegóły odznak
//                 </Typography>
//                 <Stack spacing={2}>
//                     {report?.badges?.map((badge) => (
//                         <Card key={badge.id} sx={{ p: 2, display: "flex", gap: 2 }}>
//                             <CardMedia
//                                 component="img"
//                                 src={badge.imageUrl}
//                                 alt={badge.badgeName}
//                                 sx={{ width: 60, height: 60, borderRadius: 1 }}
//                             />
//                             <Box sx={{ flex: 1 }}>
//                                 <Typography variant="subtitle1" fontWeight={700}>
//                                     {badge.badgeName}
//                                 </Typography>
//                                 <Typography variant="body2" color="text.secondary">
//                                     Typ: {badge.badgeType ?? "—"} | Status: {badge.status ?? "—"}
//                                 </Typography>
//                                 {badge.prkLevel && (
//                                     <Typography variant="body2" color="text.secondary">
//                                         Poziom PRK: {badge.prkLevel}
//                                     </Typography>
//                                 )}
//                                 {badge.qualificationName && (
//                                     <Typography variant="body2" color="text.secondary">
//                                         Kwalifikacja: {badge.qualificationName}
//                                     </Typography>
//                                 )}
//                                 {badge.expirationDate && (
//                                     <Typography variant="body2" color="text.secondary">
//                                         Wygasa: {new Date(badge.expirationDate).toLocaleDateString()}
//                                     </Typography>
//                                 )}
//                             </Box>
//                         </Card>
//                     ))}
//                 </Stack>
//             </Card>

//             {/* AI Advisor */}
//             <Box sx={{ mt: 4 }}>
//                 {/* <CareerAIAdvisorCard studentData={reportData} /> */}
//             </Box>

//             {/* Pobierz PDF */}
//             <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 4 }}>
//                 <Fab color="primary" onClick={generatePdf}>
//                     <DownloadIcon />
//                 </Fab>
//             </Box>
//             <Typography variant="body2" align="center" color="text.secondary" mb={4}>
//                 Pobierz raport w formacie PDF
//             </Typography>
//         </Box>
//     );
// };

// export default ReportSingle;
